import { useEffect, useState } from 'react';
import { useApiHolder } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { ScaffolderTask } from '@backstage/plugin-scaffolder-react';
import { declarersOf, templateAppTitle, useAppTitle } from '../shared/appTitle';
import type { AppTypeDetail } from '../shared/appTypes';
import { appTypeOf } from '../shared/projectIdentity';
import {
  roleOfTemplate,
  useAppTypeDescriptors,
  type TemplateRole,
} from '../shared/templateRoles';

/**
 * What a task page reads of its task's template: its ROLE — a ref known by
 * name's (the embedded Odoo descriptor's or a generic one), else the one the
 * AppTypes' descriptors designate it for, so that another type's own
 * templates get the same pages — and the NAME of the application it is
 * about (`shared/appTitle.ts`): the title of the one type designating the
 * template (`declarersOf` : la création Odoo par le descripteur embarqué
 * tant que les descripteurs ne sont pas lus, ou s'ils ne se lisent pas),
 * else (a generic template several types designate) the title of the
 * project's type, read from its catalog entity.
 *
 * Meanwhile, or when the descriptors cannot be read, the role is the static
 * one and the name `LEGACY_APP_TITLE`, as before the platform.
 */
export interface TaskTemplate {
  role: TemplateRole | undefined;
  appTitle: string;
  /**
   * The application's `AppType` when known: the designating type's, else
   * the project's.
   */
  appType: string | undefined;
  /** The descriptors read, for the helpers taking them (empty meanwhile). */
  appTypes: readonly AppTypeDetail[];
}

const NO_APP_TYPES: readonly AppTypeDetail[] = [];

/** The project a task names: `project`, or a creation's `projectName`. */
function projectParameter(
  task: ScaffolderTask | undefined,
): string | undefined {
  const parameters = task?.spec.parameters;
  const value = parameters?.project ?? parameters?.projectName;
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * The `AppType` of `project` from its catalog entity; undefined while
 * unread, without `project`, or when the catalog cannot answer. The catalog
 * API is optional here: only a generic template that several types
 * designate needs this read, and the task pages did not need the API
 * before it.
 */
function useProjectAppType(project: string | undefined): string | undefined {
  const catalogApi = useApiHolder().get(catalogApiRef);
  const [found, setFound] = useState<{ project: string; appType: string }>();

  useEffect(() => {
    if (project === undefined || !catalogApi) return undefined;
    let active = true;
    catalogApi
      .getEntityByRef({
        kind: 'component',
        namespace: 'default',
        name: project,
      })
      .then(
        entity => {
          const appType = entity && appTypeOf(entity);
          if (active && appType) setFound({ project, appType });
        },
        () => {
          // Unreadable catalog: the legacy name stays.
        },
      );
    return () => {
      active = false;
    };
  }, [catalogApi, project]);

  return found && found.project === project ? found.appType : undefined;
}

/** The role and the application's name of `task` (header). */
export function useTaskTemplate(
  task: ScaffolderTask | undefined,
): TaskTemplate {
  const ref = task?.spec.templateInfo?.entityRef;
  const descriptors = useAppTypeDescriptors({ enabled: task !== undefined });
  const appTypes =
    descriptors.status === 'ready' ? descriptors.items : NO_APP_TYPES;
  const fromTemplate = templateAppTitle(ref, appTypes);
  const declarers = declarersOf(ref, appTypes);
  const projectType = useProjectAppType(
    declarers.length > 1 ? projectParameter(task) : undefined,
  );
  const fromProject = useAppTitle(projectType);
  const appType = declarers.length === 1 ? declarers[0].name : projectType;
  return {
    role: roleOfTemplate(ref, appTypes),
    appTitle: fromTemplate ?? fromProject,
    appType,
    appTypes,
  };
}
